/**
 * `identityState(record)` — the single answer to "who is this visitor, and
 * did Sill check?" for every surface: audit list, Overview feed, detail
 * panel, the older detail panel and the audit bundle.
 *
 * Labels-review DECISIONS.md §Identity model (2026-10-11). Three words plus
 * people, computed ONLY from fields the server computed:
 *
 *   confirmed   — `identity_verification` is `signature` | `published_ip` |
 *                 `rdns` and no impersonation reason. The server sets
 *                 `signature` only for (a) a verified Web Bot Auth signature
 *                 from the signer the claimed operator declares in Sill's
 *                 registry (vetted public signer), or (b) a Sill-hosted
 *                 operator credential (ADR-0234) that was verified, not
 *                 replayed, on an attested report, from an approved operator
 *                 whose approved agent names include this UA's token.
 *                 `published_ip` / `rdns` are only projected for attested
 *                 reports with a Cloudflare-observed address.
 *   mismatch    — `impersonation_reason` is one of MISMATCH_REASONS. A real
 *                 cryptographic signature failure only (`sig_invalid`;
 *                 replayed / expired signatures arrive as null and never
 *                 reach here), `signer_mismatch`, `ip_outside_published_ranges`,
 *                 `robots_token_ua`, `rdns_mismatch`.
 *   person      — the edge classed the visit `human_likely` and no
 *                 signature verified and no server verdict says otherwise.
 *   unconfirmed — everything else, including signed-but-name-not-registered,
 *                 unvetted signers, operators with no published method,
 *                 empty / odd user agents and rows written before the
 *                 verdict was projected.
 *
 * `cf_verified_bot` is never read: on Worker / plugin rows it described
 * Sill's own API host, and on zones without Bot Management `false` is a
 * default, not a finding.
 *
 * The SQL filter in `services/api/src/http/routes/v1/audit.ts` implements
 * `identityFilter` with the same precedence (mismatch > confirmed > person >
 * unconfirmed); `identity-state.test.ts` pins the table both follow.
 */
import { inferUaProductToken, isSillFirstPartySigner, knownPublicSignerFor, publicSignerDisplayFor } from '@sill/types';
export const MISMATCH_REASONS = [
    'sig_invalid',
    'signer_mismatch',
    'ip_outside_published_ranges',
    'robots_token_ua',
    'rdns_mismatch',
];
/** `identity_verification` values that make a visit Confirmed. */
export const CONFIRMED_VERIFICATIONS = [
    'signature',
    'published_ip',
    'rdns',
];
const SURFACE_LINE = {
    worker: 'Seen by your Cloudflare Worker',
    plugin: 'Seen by your WordPress plugin',
    server: 'Seen by your server-side install',
};
function isMismatchReason(v) {
    return typeof v === 'string' && MISMATCH_REASONS.includes(v);
}
function isSillSigner(signed_agent) {
    return typeof signed_agent === 'string' && signed_agent.trim().toLowerCase().startsWith('edge.sill.so/agents/');
}
function nonEmpty(v) {
    return typeof v === 'string' && v.trim().length > 0 ? v : undefined;
}
function isPerson(record) {
    return record.identification_match?.visitor_class === 'human_likely' && record.signature_verified !== true;
}
/** Pure. Unknown enum values fall through to Unconfirmed. */
export function identityState(record) {
    const ua_token = inferUaProductToken(record.user_agent ?? '');
    const ev = record.identity_evidence ?? {};
    const signed_agent = nonEmpty(record.signed_agent);
    const sill_signer = isSillSigner(signed_agent);
    const sig_ok = record.signature_verified === true;
    const signer_name = sill_signer
        ? nonEmpty(record.signed_operator_display) ?? nonEmpty(ev.operator_display) ?? nonEmpty(record.signed_operator_slug)
        : (signed_agent !== undefined ? knownPublicSignerFor(signed_agent)?.display : undefined);
    const surface = typeof record.via === 'string' ? SURFACE_LINE[record.via] : undefined;
    const withSurface = (lines) => (surface !== undefined ? [...lines, surface] : lines);
    // ── Mismatch: server-enumerated reasons only ──
    const imp = record.impersonation_reason;
    if (isMismatchReason(imp)) {
        const op = nonEmpty(ev.operator_display);
        const owner = op !== undefined ? `${op}'s` : "the operator's";
        const lines = [];
        switch (imp) {
            case 'sig_invalid':
                lines.push('Signed request — the signature did not match the published key');
                break;
            case 'signer_mismatch':
                lines.push(signed_agent !== undefined
                    ? `Signed by ${signed_agent}, which ${op ?? 'the operator'} does not use for this name`
                    : `Signed by someone ${op ?? 'the operator'} does not use for this name`);
                break;
            case 'ip_outside_published_ranges':
                lines.push(`Address is not on ${owner} published list`);
                break;
            case 'robots_token_ua':
                lines.push('Uses a name that only appears in robots.txt rules, never in real requests');
                break;
            case 'rdns_mismatch':
                lines.push(`Address does not belong to ${owner} documented domain (reverse DNS)`);
                break;
        }
        return {
            state: 'mismatch',
            name: ua_token ?? 'Unknown visitor',
            ...(op !== undefined ? { operator: op } : {}),
            evidence: withSurface(lines),
            reason: imp,
        };
    }
    // ── Confirmed: server-computed positive evidence only ──
    const v = record.identity_verification;
    if (v === 'signature' || v === 'published_ip' || v === 'rdns') {
        const lines = [];
        let name = ua_token ?? signer_name ?? 'Unknown visitor';
        let operator = nonEmpty(ev.operator_display);
        if (v === 'signature' && isSillFirstPartySigner(signed_agent)) {
            // 2026-10-12 — Sill's own tool, signed with Sill's own key (ADR-0238).
            // The server only projects `signature` here when the UA token is one
            // of the Sill tools that sign (first-party credential binding).
            operator = 'Sill';
            const agent = nonEmpty(record.signed_credential_agent_display);
            if (agent !== undefined)
                name = agent;
            lines.push("Signed request — verified against Sill's own published key");
            lines.push('Sill checking your site');
        }
        else if (v === 'signature' && sill_signer) {
            operator = signer_name ?? operator;
            const agent = nonEmpty(record.signed_credential_agent_display);
            if (agent !== undefined)
                name = agent;
            lines.push(operator !== undefined
                ? `Signed request — key registered with Sill by ${operator}`
                : 'Signed request — key registered with Sill');
            lines.push(operator !== undefined
                ? `Name approved — ${operator} lists "${agent ?? ua_token ?? name}" as its agent`
                : `Name approved — the operator lists "${agent ?? ua_token ?? name}" as its agent`);
        }
        else if (v === 'signature') {
            // Vetted public signer bound to the claimed operator. A browser UA
            // (agent mode) has no product token: name the signer instead.
            const display = publicSignerDisplayFor(signed_agent, record.user_agent);
            if (display === null && ua_token === null && signer_name !== undefined)
                name = signer_name;
            operator = operator ?? signer_name;
            lines.push(signed_agent !== undefined
                ? `Signed request — verified against ${signed_agent}'s published key`
                : 'Signed request — verified against the operator\'s published key');
        }
        else if (v === 'published_ip') {
            lines.push(operator !== undefined
                ? `Address is on ${operator}'s published list`
                : "Address is on the operator's published list");
        }
        else {
            lines.push(operator !== undefined
                ? `Address belongs to ${operator} (reverse DNS)`
                : "Address belongs to the operator's documented domain (reverse DNS)");
        }
        if (v !== 'signature')
            lines.push('Not signed');
        return {
            state: 'confirmed',
            name,
            ...(operator !== undefined ? { operator } : {}),
            evidence: withSurface(lines),
        };
    }
    // ── Person ──
    if (isPerson(record)) {
        return { state: 'person', name: 'Person', evidence: [] };
    }
    // ── Unconfirmed: everything else ──
    const lines = [];
    // Security review B1: an off-list address on a list the operator does
    // not call complete is a fact worth showing, never a Mismatch.
    if (nonEmpty(ev.ip_list_url) !== undefined) {
        const op = nonEmpty(ev.operator_display);
        lines.push(op !== undefined
            ? `Not on ${op}'s published list; ${op} doesn't say the list is complete`
            : "Not on the operator's published list; the operator doesn't say the list is complete");
    }
    if (sig_ok && sill_signer) {
        const who = signer_name ?? 'a Sill-enrolled operator';
        if (record.signed_credential_name_registered === false) {
            lines.push(`Signed by ${who}; this name isn't one they registered`);
        }
        else {
            lines.push(`Signed by ${who}, but Sill could not tie the signature to this visit`);
        }
    }
    else if (sig_ok && signed_agent !== undefined) {
        const vetted = knownPublicSignerFor(signed_agent);
        // Security review m1: a row written before the verdict was projected
        // (no `identity_verification`) was never checked against the signer's
        // names, so it must not claim the signer "doesn't send this name".
        lines.push(vetted !== null && record.identity_verification === undefined
            ? `Signed by ${vetted.display}; not checked for this visit`
            : vetted !== null
                ? `Signed by ${vetted.display} (${signed_agent}); ${vetted.display} doesn't send this name`
                : `Signed by ${signed_agent}, a signer Sill hasn't vetted`);
    }
    else if (record.signature_verified === false) {
        lines.push('Signed request — Sill could not check the signature');
    }
    else {
        lines.push('Not signed');
    }
    return {
        state: 'unconfirmed',
        name: ua_token ?? (sig_ok ? signer_name : undefined) ?? 'Unknown visitor',
        evidence: withSurface(lines),
    };
}
/**
 * Does `record` belong in `filter`? `bots` = everything except people.
 * Mirrors the SQL predicate in the audit list route.
 */
export function identityFilter(record, filter) {
    if (filter === 'all')
        return true;
    const s = identityState(record).state;
    switch (filter) {
        case 'confirmed': return s === 'confirmed';
        case 'mismatch': return s === 'mismatch';
        case 'unconfirmed': return s === 'unconfirmed';
        case 'people': return s === 'person';
        case 'bots': return s !== 'person';
        default: return true;
    }
}
//# sourceMappingURL=identity-state.js.map